import { CircleXmarkFill } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { SignInIdentifier } from '@logto/schemas';
import type { Nullable } from '@silverhand/essentials';
import classNames from 'classnames';
import type { HTMLProps, Ref } from 'react';
import { useEffect, useId, useImperativeHandle, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import usePasskeyAutofillConditionalUI from '@/hooks/use-passkey-autofill-conditional-ui';
import InputField, {
  fieldButtonHitAreaClass,
  getInputLabelId,
} from '@/shared/components/InputFields/InputField';

import CountryCodeSelector from './CountryCodeSelector';
import type { IdentifierInputType, IdentifierInputValue } from './use-smart-input-field';
import useSmartInputField from './use-smart-input-field';
import { getInputHtmlProps, getPhoneExample } from './utils';

export type { IdentifierInputType, IdentifierInputValue } from './use-smart-input-field';

type Props = Omit<HTMLProps<HTMLInputElement>, 'onChange' | 'prefix' | 'value' | 'ref'> & {
  readonly className?: string;
  readonly errorMessage?: string;
  readonly isDanger?: boolean;
  readonly enabledTypes?: IdentifierInputType[];
  readonly defaultValue?: string;
  readonly onChange?: (data: IdentifierInputValue) => void;
  readonly ref?: Ref<Nullable<HTMLInputElement>>;
};

const SmartInputField = ({ defaultValue, enabledTypes = [], onChange, ref, ...rest }: Props) => {
  const { t } = useTranslation();
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle<Nullable<HTMLInputElement>, Nullable<HTMLInputElement>>(
    ref,
    () => innerRef.current
  );
  const isInputEditable = !rest.readOnly && !rest.disabled;
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;

  const {
    countryCode,
    onCountryCodeChange,
    inputValue,
    onInputValueChange,
    onInputValueClear,
    identifierType,
  } = useSmartInputField({
    defaultValue,
    enabledTypes,
  });

  const isPrefixVisible = identifierType === SignInIdentifier.Phone;

  useEffect(() => {
    onChange?.({
      type: identifierType,
      value: isPrefixVisible && inputValue ? `${countryCode}${inputValue}` : inputValue,
    });
  }, [countryCode, identifierType, inputValue, isPrefixVisible, onChange]);

  const { isPasskeyAutofillEnabled, abortConditionalUI } = usePasskeyAutofillConditionalUI();

  const inputHtmlProps = useMemo(() => {
    const { autoComplete, placeholder, ...rest } = getInputHtmlProps(enabledTypes, identifierType);
    return {
      ...rest,
      placeholder:
        enabledTypes[0] === SignInIdentifier.Phone
          ? (getPhoneExample(countryCode) ?? placeholder)
          : placeholder,
      // Keep the field-type token (email/tel/username) next to `webauthn` so both email and
      // passkey autofill keep working.
      autoComplete: isPasskeyAutofillEnabled ? `${autoComplete} webauthn` : autoComplete,
    };
  }, [countryCode, enabledTypes, identifierType, isPasskeyAutofillEnabled]);

  useEffect(() => {
    return () => {
      abortConditionalUI();
    };
  }, [abortConditionalUI]);

  return (
    <InputField
      {...inputHtmlProps}
      {...rest}
      ref={innerRef}
      id={inputId}
      isSuffixFocusVisible={isInputEditable && Boolean(inputValue)}
      value={inputValue}
      isPrefixVisible={isPrefixVisible}
      prefix={
        isPrefixVisible ? (
          <CountryCodeSelector
            value={countryCode}
            inputRef={innerRef}
            labelId={getInputLabelId(inputId)}
            isInteractive={isInputEditable}
            onChange={(value) => {
              onCountryCodeChange(value);
              innerRef.current?.focus();
            }}
          />
        ) : undefined
      }
      suffix={
        isInputEditable && inputValue ? (
          <Button
            isIconOnly
            preventFocusOnPress
            excludeFromTabOrder
            size="sm"
            variant="ghost"
            aria-label={t('action.remove')}
            className={classNames('text-muted', fieldButtonHitAreaClass)}
            onPress={onInputValueClear}
          >
            <CircleXmarkFill />
          </Button>
        ) : undefined
      }
      onChange={isInputEditable ? onInputValueChange : undefined}
    />
  );
};

export default SmartInputField;
